import { useState, useEffect } from 'react';
import { getPlatformServices } from '../services/platform';
import type { UpdateInfo } from '../services/platform';
import '../styles/UpdateChecker.css';

interface UpdateCheckerProps {
  isAdmin: boolean;
}

export default function UpdateChecker({ isAdmin }: UpdateCheckerProps) {
  // Platform-agnostic update service: Electron installer self-update, Android
  // OTA web-bundle swap, or null on web (display-only).
  const updateService = getPlatformServices().update;
  const [version, setVersion] = useState<string>('');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load current version on mount
  useEffect(() => {
    if (updateService) {
      updateService.getVersion().then(setVersion);
    }
  }, [updateService]);

  // Check for updates
  const checkForUpdates = async () => {
    if (!updateService) {
      setError('Update functionality not available');
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const info = await updateService.check();
      setUpdateInfo(info);
      if (!info.latestVersion) {
        setError('Could not check for updates.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check for updates');
    } finally {
      setIsChecking(false);
    }
  };

  // Install update
  const installUpdate = async () => {
    if (!updateService || !updateInfo?.downloadUrl || !updateInfo?.assetName) {
      setError('Update information not available');
      return;
    }

    if (!confirm(`Download and install version ${updateInfo.latestVersion}?\n\nThe application will restart after installation.`)) {
      return;
    }

    setIsInstalling(true);
    setError(null);

    try {
      const result = await updateService.install(updateInfo.downloadUrl, updateInfo.assetName, updateInfo.assetId);
      if (!result.success) {
        setError(result.error || 'Installation failed');
        setIsInstalling(false);
      }
      // If successful, app will quit/restart and the update will apply
    } catch (err: any) {
      setError(err.message || 'Failed to install update');
      setIsInstalling(false);
    }
  };

  // Format file size
  const formatSize = (bytes?: number): string => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Only show for admin users, and only where self-update is possible
  // (Electron / Android — not the display-only web build).
  if (!isAdmin || !updateService) {
    return null;
  }

  return (
    <div className="update-checker">
      <button
        className={`version-badge ${updateInfo?.updateAvailable ? 'has-update' : ''}`}
        onClick={() => setShowDropdown(!showDropdown)}
        title="Version info & updates"
      >
        v{version || '...'}
        {updateInfo?.updateAvailable && <span className="update-dot" />}
      </button>

      {showDropdown && (
        <div className="update-dropdown">
          <div className="update-dropdown-header">
            <span>Version Info</span>
            <button className="close-btn" onClick={() => setShowDropdown(false)}>&times;</button>
          </div>

          <div className="update-dropdown-content">
            <div className="version-row">
              <span className="label">Current:</span>
              <span className="value">v{version}</span>
            </div>

            {updateInfo && updateInfo.latestVersion && (
              <div className="version-row">
                <span className="label">Latest:</span>
                <span className={`value ${updateInfo.updateAvailable ? 'highlight' : ''}`}>
                  v{updateInfo.latestVersion}
                </span>
              </div>
            )}

            {updateInfo?.publishedAt && (
              <div className="version-row">
                <span className="label">Released:</span>
                <span className="value">{formatDate(updateInfo.publishedAt)}</span>
              </div>
            )}

            {updateInfo?.assetSize && (
              <div className="version-row">
                <span className="label">Size:</span>
                <span className="value">{formatSize(updateInfo.assetSize)}</span>
              </div>
            )}

            {error && <div className="update-error">{error}</div>}

            <div className="update-actions">
              {!updateInfo?.updateAvailable && (
                <button
                  className="btn btn-check"
                  onClick={checkForUpdates}
                  disabled={isChecking}
                >
                  {isChecking ? 'Checking...' : 'Check for Updates'}
                </button>
              )}

              {updateInfo?.updateAvailable && updateInfo.downloadUrl && (
                <button
                  className="btn btn-update"
                  onClick={installUpdate}
                  disabled={isInstalling}
                >
                  {isInstalling ? 'Installing...' : `Update to v${updateInfo.latestVersion}`}
                </button>
              )}

              {updateInfo && !updateInfo.updateAvailable && updateInfo.latestVersion && (
                <div className="up-to-date">You're up to date!</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
